import React, { useState } from 'react';
import { generatePrashnaKundali } from '../utils/vedicEngine';
import { Language, PrashnaResult } from '../types/kundali';
import { HelpCircle, RefreshCw, CheckCircle2, AlertTriangle, Sparkles, BookOpen } from 'lucide-react';
import { getPlanetFullName, getZodiacName } from '../utils/language';

interface PrashnaModuleProps {
  lang?: Language;
}

export const PrashnaModule: React.FC<PrashnaModuleProps> = ({ lang = 'en' }) => {
  const isHi = lang === 'hi';
  const [seedNumber, setSeedNumber] = useState<number>(45);
  const [category, setCategory] = useState<string>('Career & Job');
  const [prashnaResult, setPrashnaResult] = useState<PrashnaResult>(() =>
    generatePrashnaKundali(45, 'Career & Job')
  );

  const categories = [
    { key: 'Career & Job', labelEn: 'Career & Job', labelHi: 'आजीविका एवं नौकरी' },
    { key: 'Marriage & Love', labelEn: 'Marriage & Love', labelHi: 'विवाह एवं प्रेम संबंध' },
    { key: 'Health & Disease Recovery', labelEn: 'Health & Disease Recovery', labelHi: 'स्वास्थ्य एवं रोग मुक्ति' },
    { key: 'Finance & Investments', labelEn: 'Finance & Investments', labelHi: 'वित्त, धन एवं निवेश' },
    { key: 'Higher Studies & Visa', labelEn: 'Higher Studies & Visa', labelHi: 'उच्च शिक्षा एवं विदेश यात्रा/वीजा' },
    { key: 'Property & Home', labelEn: 'Property & Home', labelHi: 'भूमि, भवन एवं संपत्ति' },
    { key: 'Children & Education', labelEn: 'Children & Education', labelHi: 'संतान एवं विद्या' }
  ];

  const handleGenerate = () => {
    const res = generatePrashnaKundali(seedNumber, category);
    setPrashnaResult(res);
  };

  const handleRandomSeed = () => {
    const rand = Math.floor(Math.random() * 249) + 1;
    setSeedNumber(rand);
    const res = generatePrashnaKundali(rand, category);
    setPrashnaResult(res);
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-astro-primary/10 border border-astro-primary/20 text-astro-primary font-bold flex items-center justify-center text-xl font-serif">
            <HelpCircle className="w-6 h-6 text-astro-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-astro-text font-serif">
                {isHi ? 'पराशर / के.पी. प्रश्न कुंडली प्रणाली' : 'Parashara Horary (Prashna) Engine'}
              </h2>
              <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2 py-0.5 rounded-md">
                {isHi ? 'प्रश्न शास्त्र' : 'KP / Parashari Horary'}
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text">
              {isHi
                ? 'तात्कालिक प्रश्न विचार एवं समाधान (बीज संख्या 1 से 249)'
                : 'Instant Horary Astrology & Query Resolution (Seed Numbers 1 to 249)'}
            </p>
          </div>
        </div>
      </div>

      {/* Query Selector Panel */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-bold text-astro-primary font-serif text-base border-b border-astro-border pb-3 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-astro-primary" /> {isHi ? 'प्रश्न विषय एवं बीज संख्या चयन' : 'Select Prashna Query Parameters'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Query Category */}
          <div>
            <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
              {isHi ? 'प्रश्न का विषय चुनें' : 'Select Prashna Domain / Topic'}
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-astro-card border border-astro-border text-astro-text rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-astro-primary"
            >
              {categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {isHi ? c.labelHi : c.labelEn}
                </option>
              ))}
            </select>
          </div>

          {/* Seed Number Input */}
          <div>
            <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
              {isHi ? 'प्रश्न संख्या (1 से 249)' : 'Prashna Number (1 to 249)'}
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                max={249}
                value={seedNumber}
                onChange={(e) => setSeedNumber(Math.max(1, Math.min(249, parseInt(e.target.value) || 1)))}
                className="w-full bg-astro-card border border-astro-border text-astro-text rounded-xl px-3 py-2 text-xs font-mono focus:outline-none focus:border-astro-primary"
              />
              <button
                onClick={handleRandomSeed}
                className="px-3 py-2 bg-astro-card hover:bg-astro-border border border-astro-border text-astro-text text-xs font-semibold rounded-xl flex items-center gap-1 shrink-0 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5 text-astro-primary" /> {isHi ? 'यादृच्छिक (Random)' : 'Random'}
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={handleGenerate}
          className="w-full py-2.5 bg-astro-primary hover:bg-astro-primary/90 text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center justify-center gap-2"
        >
          <Sparkles className="w-4 h-4" /> {isHi ? 'प्रश्न कुंडली गणना एवं फलादेश देखें' : 'Calculate Prashna Horary Kundali & Verdict'}
        </button>
      </div>

      {/* Prashna Result Card */}
      {prashnaResult && (
        <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm space-y-5 animate-fade-in">
          {/* Verdict Banner */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-astro-border pb-4">
            <div>
              <span className="text-[10px] uppercase font-bold text-astro-primary bg-astro-primary/10 border border-astro-primary/20 px-2.5 py-0.5 rounded-full">
                {isHi ? 'प्रश्न फलादेश' : 'Prashna Horary Verdict'}
              </span>
              <h3 className="text-xl font-bold text-astro-text font-serif mt-1">
                {isHi
                  ? (prashnaResult.verdict.includes('Favorable') ? 'कार्य सिद्धि के पूर्ण योग (सकारात्मक फल)' : prashnaResult.verdict.includes('Moderate') ? 'मध्यम फल (प्रयास उपरांत सफलता)' : 'प्रतिकूल परिस्थिति (सावधानी एवं उपाय आवश्यक)')
                  : prashnaResult.verdict}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'विषय:' : 'Query:'} <span className="text-astro-text font-semibold">{isHi ? (categories.find(c => c.key === prashnaResult.category)?.labelHi || prashnaResult.category) : prashnaResult.category}</span> • {isHi ? 'बीज संख्या #' : 'Prashna Seed #'}{prashnaResult.horaryNumber}
              </p>
            </div>

            <div className="flex items-center gap-3 bg-astro-card px-4 py-3 rounded-xl border border-astro-border">
              <div className="text-right">
                <span className="text-[10px] uppercase text-astro-secondary-text block">{isHi ? 'अनुकूलता अंक' : 'Horary Score'}</span>
                <span className="text-lg font-bold text-astro-primary font-mono">{prashnaResult.verdictScore}/100</span>
              </div>
            </div>
          </div>

          {/* Key Horary Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text text-[10px] uppercase block">{isHi ? 'प्रश्न लग्न' : 'Prashna Lagna'}</span>
              <span className="font-bold text-astro-primary">{getZodiacName(prashnaResult.lagnaSign, lang)}</span>
              <span className="text-[10px] text-astro-secondary-text block">{isHi ? 'स्वामी:' : 'Lord:'} {getPlanetFullName(prashnaResult.lagnaLord as any, lang)}</span>
            </div>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text text-[10px] uppercase block">{isHi ? 'प्रश्न चंद्र' : 'Horary Moon'}</span>
              <span className="font-bold text-astro-text">{getZodiacName(prashnaResult.moonSign, lang)}</span>
              <span className="text-[10px] text-astro-secondary-text block">{isHi ? 'भाव' : 'House'} {prashnaResult.moonHouse}</span>
            </div>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text text-[10px] uppercase block">{isHi ? 'कार्येश भाव' : 'Target Bhava'}</span>
              <span className="font-bold text-astro-primary">{isHi ? 'भाव' : 'House'} {prashnaResult.targetHouse}</span>
            </div>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
              <span className="text-astro-secondary-text text-[10px] uppercase block">{isHi ? 'प्रश्न काल' : 'Prashna Time'}</span>
              <span className="font-mono text-astro-text font-semibold">{prashnaResult.prashnaTime}</span>
            </div>
          </div>

          {/* Astrological Analysis Factors */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-astro-primary">
              {isHi ? 'ज्योतिषीय कारक एवं प्रभाव' : 'Astrological Significators & Influences'}
            </h4>
            <div className="space-y-1.5">
              {prashnaResult.keyFactors.map((factor, idx) => (
                <div key={idx} className="p-2.5 bg-astro-card rounded-xl border border-astro-border text-xs flex items-center gap-2 text-astro-text">
                  <CheckCircle2 className="w-4 h-4 text-astro-primary shrink-0" />
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Horary Remedies */}
          <div className="p-4 bg-astro-card rounded-xl border border-astro-border space-y-2 text-xs">
            <h4 className="font-bold text-astro-primary font-serif flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-astro-primary" /> {isHi ? 'प्रश्न शास्त्र अनुसार उपाय एवं मार्गदर्शन' : 'Prashna Horary Remedies & Recommendations'}
            </h4>
            <ul className="list-disc list-inside text-astro-secondary-text space-y-1">
              {prashnaResult.remedies.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
